import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { getAllUserOrders } from "../../services/order.service";
import { MdArrowBack, MdDownload, MdPrint } from "react-icons/md";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { getBusiness } from "../../services/auth.service";
import { AWS_URL } from "../../services/http.service";
import { useSelector } from "react-redux";
import { LogoLoading } from "../../components/common/utlity/utility";
import { getStorefrontPath } from "./storefrontPaths";

function InvoicePage() {
  const { businessName: urlBusinessName, bussinessUuid, orderId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [store, setStore] = useState(null);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const invoiceRef = useRef();
  const autoDownloadTriggeredRef = useRef(false);
  const businessData = useSelector((state) => state.bussiness.business);
  const business = Array.isArray(businessData)
    ? businessData.find((b) => b.bussinessUuid === bussinessUuid) ||
      businessData[0]
    : businessData;
  const activeStore = store || business || {};
  const displayBusinessName =
    urlBusinessName || activeStore.businessName || "Store";
  const isB2BMode = localStorage.getItem("b2b_Price") === "true";

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const userProfile = JSON.parse(
          localStorage.getItem(`user_${bussinessUuid}`),
        );
        const userId = userProfile?.customerId;

        if (!userId) {
          setError("Please log in to view your invoice");
          setLoading(false);
          return;
        }
        const result = await getBusiness(bussinessUuid);
        console.log("line 33 store in invoice", result);
        if (result.data) {
          setStore(result.data);
        }
        const res = await getAllUserOrders(userId, bussinessUuid);
        if (!res.data || !Array.isArray(res.data)) {
          throw new Error("Invalid response format from server");
        }

        const foundOrder = res.data.find((o) => o.orderId === orderId);
        if (foundOrder) {
          const parsedOrder = {
            ...foundOrder,
            items:
              typeof foundOrder.items === "string"
                ? JSON.parse(foundOrder.items)
                : foundOrder.items,
          };
          setOrder(parsedOrder);
        } else {
          setError("Order not found");
        }
      } catch (err) {
        setError(err.message || "Failed to fetch order details");
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [bussinessUuid, orderId]);
  console.log("order", order);
  const searchParams = new URLSearchParams(location.search);
  const shouldAutoDownload = searchParams.get("download") === "pdf";
  const closeAfterDownload = searchParams.get("closeAfterDownload") === "1";
  const requestedInvoiceId = searchParams.get("invoiceId") || "";
  const requestedBatchId = searchParams.get("batchId") || "";

  const downloadInvoice = async (options = {}) => {
    setDownloading(true);
    try {
      const element = invoiceRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(`invoice-${order.orderId}.pdf`);

      if (options.closeAfterDownload && window.opener) {
        window.close();
      }
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    if (!order || !shouldAutoDownload || autoDownloadTriggeredRef.current) {
      return;
    }

    autoDownloadTriggeredRef.current = true;
    downloadInvoice({ closeAfterDownload });
  }, [order, shouldAutoDownload, closeAfterDownload]);

  if (loading) {
    return <LogoLoading />;
  }

  if (error) {
    return (
      <div className='min-h-screen bg-gray-100 flex items-center justify-center p-3 sm:p-4'>
        <div className='bg-white rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-md mx-auto text-center'>
          <p className='text-red-500 text-sm sm:text-base md:text-lg'>
            {error}
          </p>
          {error.includes("log in") && (
            <button
              onClick={() =>
                navigate(
                  getStorefrontPath({
                    businessName: displayBusinessName,
                    bussinessUuid,
                  }),
                )
              }
              className='mt-3 sm:mt-4 flex items-center justify-center gap-1.5 sm:gap-2 text-blue-600 hover:text-blue-800 text-xs sm:text-sm md:text-base font-medium transition-colors'
            >
              <MdArrowBack className='text-sm sm:text-base' />
              Go to Login
            </button>
          )}
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className='min-h-screen bg-gray-100 flex items-center justify-center p-3 sm:p-4'>
        <div className='bg-white rounded-lg shadow-lg p-4 sm:p-6 w-full max-w-md mx-auto text-center'>
          <p className='text-gray-600 text-sm sm:text-base md:text-lg'>
            No order found.
          </p>
        </div>
      </div>
    );
  }

  const directInvoices = Array.isArray(order?.partialDelivery?.invoices)
    ? order.partialDelivery.invoices
    : [];
  const batchInvoices = Array.isArray(order?.partialDelivery?.batches)
    ? order.partialDelivery.batches
        .filter((batch) => batch?.invoiceId)
        .map((batch) => ({
          batchId: batch?.batchId || "",
          invoiceId: batch?.invoiceId || "",
          saleId: batch?.saleId || "",
          amount: Number(batch?.invoiceAmount || 0),
          status: batch?.status || "delivered",
          createdAt: batch?.deliveredAt || batch?.assignedAt || "",
          items: Array.isArray(batch?.items)
            ? batch.items.map((item) => ({
                productId: item?.productId || "",
                productName: item?.productName || "Product",
                variantId: item?.variantId || "",
                variantName: item?.variantName || "",
                quantity: Number(item?.quantity || 0),
                salePrice: Number(item?.salePrice || item?.price || 0),
                total: Number(
                  (
                    Number(item?.total || 0) ||
                    Number(item?.salePrice || item?.price || 0) *
                      Number(item?.quantity || 0)
                  ).toFixed(2),
                ),
              }))
            : [],
        }))
    : [];

  const mergedDeliveryInvoices = [...directInvoices, ...batchInvoices].reduce(
    (acc, invoice) => {
      const key =
        String(invoice?.invoiceId || "") ||
        String(invoice?.saleId || "") ||
        String(invoice?.batchId || "");

      if (
        !key ||
        acc.some(
          (entry) =>
            String(entry?.invoiceId || "") ===
              String(invoice?.invoiceId || "") &&
            String(entry?.batchId || "") === String(invoice?.batchId || ""),
        )
      ) {
        return acc;
      }

      acc.push(invoice);
      return acc;
    },
    [],
  );

  const selectedDeliveryInvoice =
    requestedInvoiceId || requestedBatchId
      ? mergedDeliveryInvoices.find((invoice) => {
          const invoiceMatches = requestedInvoiceId
            ? String(invoice?.invoiceId || "") === String(requestedInvoiceId)
            : true;
          const batchMatches = requestedBatchId
            ? String(invoice?.batchId || "") === String(requestedBatchId)
            : true;
          return invoiceMatches && batchMatches;
        }) || null
      : null;
  const isDeliveryInvoiceMode = Boolean(selectedDeliveryInvoice);

  const deriveDeliveryInvoiceItems = (invoice) => {
    const sourceItems = Array.isArray(invoice?.items) ? invoice.items : [];
    const invoiceAmount = Number(invoice?.amount || 0);

    return sourceItems.map((item) => {
      const quantity = Math.max(
        0,
        Number(item?.quantity || item?.cartQuantity || 0),
      );
      const directUnitPrice = Number(item?.salePrice || item?.price || 0);
      const directLineTotal = Number(item?.total || 0);

      const resolvedLineTotal =
        directLineTotal > 0
          ? directLineTotal
          : quantity > 0 && directUnitPrice > 0
            ? Number((directUnitPrice * quantity).toFixed(2))
            : sourceItems.length === 1
              ? invoiceAmount
              : 0;

      const resolvedUnitPrice =
        directUnitPrice > 0
          ? directUnitPrice
          : quantity > 0 && resolvedLineTotal > 0
            ? Number((resolvedLineTotal / quantity).toFixed(2))
            : 0;

      return {
        ...item,
        cartQuantity: quantity,
        quantity,
        salePrice: resolvedUnitPrice,
        total: resolvedLineTotal,
      };
    });
  };

  const invoiceItems = isDeliveryInvoiceMode
    ? deriveDeliveryInvoiceItems(selectedDeliveryInvoice)
    : Array.isArray(order?.items)
      ? order.items
      : [];

  const invoiceNumber = selectedDeliveryInvoice?.invoiceId || order.orderId;
  const invoiceDate = isDeliveryInvoiceMode
    ? selectedDeliveryInvoice?.createdAt || order.orderDate
    : order.orderDate;

  const invoiceLineSubtotal = invoiceItems.reduce((sum, item) => {
    const qty = Number(item?.cartQuantity || item?.quantity || 1);
    const lineTotal = Number(item?.total || 0);
    if (lineTotal > 0) {
      return sum + lineTotal;
    }

    const unitPrice = isB2BMode
      ? Number(item?.b2bSalePrice || 0)
      : Number(item?.salePrice || 0);

    return sum + unitPrice * qty;
  }, 0);

  // Calculate tax breakdown
  const taxBreakdown = {
    cgst: invoiceItems.reduce(
      (sum, item) => sum + (item.taxBreakup?.cgst || 0),
      0,
    ),
    sgst: invoiceItems.reduce(
      (sum, item) => sum + (item.taxBreakup?.sgst || 0),
      0,
    ),
    igst: invoiceItems.reduce(
      (sum, item) => sum + (item.taxBreakup?.igst || 0),
      0,
    ),
    cess: invoiceItems.reduce(
      (sum, item) => sum + (item.taxBreakup?.cess || 0),
      0,
    ),
  };

  const subtotal = Number(invoiceLineSubtotal || 0);
  const shippingAmount = isDeliveryInvoiceMode
    ? Number(selectedDeliveryInvoice?.shippingAmount || 0)
    : Number(order?.shippingCost || order?.shippingCharges || 0);
  const taxFromInvoice = isDeliveryInvoiceMode
    ? Number(selectedDeliveryInvoice?.taxAmount || 0)
    : 0;
  const taxFromOrder = !isDeliveryInvoiceMode
    ? Math.max(0, Number(order?.total || 0) - subtotal - shippingAmount)
    : 0;
  const taxBreakupTotal =
    Number(taxBreakdown.cgst || 0) +
    Number(taxBreakdown.sgst || 0) +
    Number(taxBreakdown.igst || 0) +
    Number(taxBreakdown.cess || 0);
  const effectiveTaxAmount = Number(
    (taxBreakupTotal > 0 ? taxBreakupTotal : taxFromInvoice || taxFromOrder) ||
      0,
  );
  const discountTexts = invoiceItems
    .map((item) => {
      const discountValue = parseFloat(item.discount) || 0;
      const discountUnit = item.discountUnit?.trim() || "";
      return `${discountValue.toFixed(2)}${discountUnit}`;
    })
    .filter((text) => text !== "0.00");

  return (
    <div className='min-h-screen bg-gray-100 p-2 sm:p-4 md:p-6'>
      <div className='max-w-3xl mx-auto'>
        {/* Action buttons */}
        <div className='flex justify-between items-center mb-3 sm:mb-4'>
          {!shouldAutoDownload ? (
            <button
              onClick={() =>
                navigate(
                  getStorefrontPath({
                    businessName: displayBusinessName,
                    bussinessUuid,
                    suffix: "orders",
                  }),
                )
              }
              className='flex items-center gap-1.5 sm:gap-2 text-blue-600 hover:text-blue-800 font-medium transition-colors text-xs sm:text-sm md:text-base'
            >
              <MdArrowBack className='text-sm sm:text-base md:text-lg' />
              <span className='hidden xs:inline'>Back</span>
            </button>
          ) : (
            <div />
          )}
          <div className='flex gap-1.5 sm:gap-2'>
            <button
              onClick={() => downloadInvoice()}
              disabled={downloading}
              className='flex items-center gap-1.5 sm:gap-2 bg-blue-600 text-white hover:bg-blue-700 px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-lg transition-colors disabled:opacity-50 text-xs sm:text-sm md:text-base'
            >
              {downloading ? (
                <>
                  <div className='animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-t-2 border-b-2 border-white'></div>
                  <span className='hidden sm:inline'>Generating...</span>
                  <span className='sm:hidden'>Saving...</span>
                </>
              ) : (
                <>
                  <MdDownload className='text-sm sm:text-base md:text-lg' />
                  <span className='hidden xs:inline'>Download PDF</span>
                  <span className='xs:hidden'>PDF</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Invoice */}
        <div
          ref={invoiceRef}
          className='bg-white rounded-lg shadow-lg p-3 sm:p-5 md:p-6 lg:p-8 font-sans'
        >
          {/* Header - Responsive */}
          <div className='flex flex-col sm:flex-row justify-between items-center border-b border-gray-200 pb-4 sm:pb-5 md:pb-6 mb-4 sm:mb-5 md:mb-6 gap-3 sm:gap-0'>
            {/* Left: Invoice + Order */}
            <div className='text-center sm:text-left w-full sm:w-auto'>
              <h1 className='text-xl sm:text-2xl font-bold text-gray-800'>
                INVOICE
              </h1>
              <p className='text-gray-600 mt-0.5 sm:mt-1 text-xs sm:text-sm'>
                Order #{order.orderId}
              </p>
              {isDeliveryInvoiceMode && (
                <p className='text-gray-600 mt-0.5 sm:mt-1 text-xs sm:text-sm'>
                  Delivery Invoice #{invoiceNumber}
                </p>
              )}
            </div>

            {/* Middle: Business Logo */}
            <div className='my-3 sm:my-0'>
              <div className='rounded-full overflow-hidden flex items-center justify-center bg-gray-100 shadow-md h-10 w-10 sm:h-11 sm:w-11 md:h-12 md:w-12'>
                <img
                  src={
                    activeStore.businesslogo
                      ? AWS_URL + activeStore.businesslogo
                      : "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT3dSEtDvpd9KAn4SdFJVoL0K1lj4Xfc-wVWQ&s"
                  }
                  alt='Logo'
                  className='h-full w-full object-contain'
                  // crossOrigin='anonymous'
                />
              </div>
            </div>

            {/* Right: Branding */}
            <div className='text-center sm:text-right w-full sm:w-auto'>
              <p className='text-gray-800 font-semibold text-sm sm:text-base'>
                Dukanbuddy™
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                Powered by Dukanbuddy
              </p>
            </div>
          </div>

          {/* Company and Customer Details - Responsive Grid */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5 md:gap-6 mb-6 sm:mb-7 md:mb-8'>
            <div>
              <h2 className='text-base sm:text-lg font-semibold text-gray-800 mb-1.5 sm:mb-2'>
                Business Information
              </h2>
              <p className='text-gray-600 text-xs sm:text-sm'>
                {store.businessName || "Store Name"}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                {(store?.firstLine, store?.pincode || "Business Address")}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                {store?.phone || "Phone: N/A"}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                {store?.email || "Email: N/A"}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                GSTIN: {store?.gstNumber || "N/A"}
              </p>
            </div>

            <div>
              <h2 className='text-base sm:text-lg font-semibold text-gray-800 mb-1.5 sm:mb-2'>
                Bill To
              </h2>
              <p className='text-gray-600 text-xs sm:text-sm'>
                {order.customerName || "N/A"}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                {order.address || "N/A"}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                {order.city || "N/A"}, {order.state || "N/A"} -{" "}
                {order.pincode || "N/A"}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm'>
                Phone: {order.phone || "N/A"}
              </p>
            </div>
          </div>

          {/* Order Details - Responsive */}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6 sm:mb-7 md:mb-8'>
            <div>
              <p className='text-gray-600 text-xs sm:text-sm'>
                <span className='font-semibold'>Invoice Number:</span>{" "}
                {invoiceNumber}
              </p>
              <p className='text-gray-600 text-xs sm:text-sm mt-1'>
                <span className='font-semibold'>Invoice Date:</span>{" "}
                {invoiceDate || "N/A"}
              </p>
            </div>
            <div>
              <p className='text-gray-600 text-xs sm:text-sm'>
                <span className='font-semibold'>Order Status:</span>{" "}
                <span className='capitalize'>
                  {order.orderStatus?.toLowerCase() || "N/A"}
                </span>
              </p>
              {isDeliveryInvoiceMode && (
                <p className='text-gray-600 text-xs sm:text-sm mt-1'>
                  <span className='font-semibold'>Delivery Type:</span> Partial
                  Delivery Batch
                </p>
              )}
              <p className='text-gray-600 text-xs sm:text-sm mt-1'>
                <span className='font-semibold'>Payment Method:</span>{" "}
                {order.paymentMethod || "N/A"}
              </p>
            </div>
          </div>

          {/* Items Table - Responsive */}
          <div className='mb-6 sm:mb-7 md:mb-8 overflow-x-auto -mx-3 sm:mx-0'>
            <div className='min-w-[500px] sm:min-w-0 px-3 sm:px-0'>
              <table className='w-full border-collapse'>
                <thead>
                  <tr className='bg-gray-100'>
                    <th className='py-2 sm:py-3 px-2 sm:px-3 md:px-4 text-left font-semibold text-gray-700 border-b text-[10px] sm:text-xs md:text-sm'>
                      Item
                    </th>
                    <th className='py-2 sm:py-3 px-2 sm:px-3 md:px-4 text-center font-semibold text-gray-700 border-b text-[10px] sm:text-xs md:text-sm'>
                      Qty
                    </th>
                    <th className='py-2 sm:py-3 px-2 sm:px-3 md:px-4 text-right font-semibold text-gray-700 border-b text-[10px] sm:text-xs md:text-sm'>
                      Unit Price
                    </th>
                    <th className='py-2 sm:py-3 px-2 sm:px-3 md:px-4 text-right font-semibold text-gray-700 border-b text-[10px] sm:text-xs md:text-sm'>
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceItems.length > 0 ? (
                    invoiceItems.map((item, idx) => (
                      <tr
                        key={idx}
                        className={idx % 2 === 0 ? "bg-gray-50" : ""}
                      >
                        <td className='py-2 sm:py-3 px-2 sm:px-3 md:px-4 border-b'>
                          <div>
                            <p className='font-medium text-gray-800 text-xs sm:text-sm'>
                              {item.productName || "N/A"}
                            </p>
                            <p className='text-[10px] sm:text-xs text-gray-600 mt-0.5'>
                              {item.variantName
                                ? `Variant: ${item.variantName}`
                                : `SKU: ${item.sku || item.sku_number || "N/A"}`}
                            </p>
                          </div>
                        </td>
                        <td className='py-2 sm:py-3 px-2 sm:px-3 md:px-4 text-center border-b text-xs sm:text-sm'>
                          {item.cartQuantity || 1}
                        </td>
                        <td className='py-2 sm:py-3 px-2 sm:px-3 md:px-4 text-right border-b text-xs sm:text-sm'>
                          ₹
                          {isB2BMode
                            ? item.b2bSalePrice
                            : item.salePrice || "0.00"}
                        </td>
                        <td className='py-2 sm:py-3 px-2 sm:px-3 md:px-4 text-right border-b text-xs sm:text-sm'>
                          ₹
                          {(
                            Number(item?.total || 0) ||
                            (isB2BMode
                              ? (item.b2bSalePrice ?? item.salePrice)
                              : item.salePrice) * (item.cartQuantity || 1)
                          ).toFixed(2)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan='4'
                        className='py-3 sm:py-4 px-3 sm:px-4 text-center text-gray-600 border-b text-xs sm:text-sm'
                      >
                        No items found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals - Responsive */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5 md:gap-6'>
            <div>
              <h3 className='text-base sm:text-lg font-semibold text-gray-800 mb-1.5 sm:mb-2'>
                Notes
              </h3>
              <p className='text-gray-600 text-xs sm:text-sm'>
                Thank you for your business. Please make payment within 15 days
                of receiving this invoice.
              </p>
            </div>

            <div>
              <div className='bg-gray-50 rounded-lg p-3 sm:p-4'>
                <div className='flex justify-between py-1.5 sm:py-2'>
                  <span className='text-gray-600 text-xs sm:text-sm'>
                    Subtotal:
                  </span>
                  <span className='text-gray-800 text-xs sm:text-sm'>
                    ₹{subtotal.toFixed(2)}
                  </span>
                </div>

                {taxBreakdown.cgst > 0 && (
                  <div className='flex justify-between py-1.5 sm:py-2'>
                    <span className='text-gray-600 text-xs sm:text-sm'>
                      CGST:
                    </span>
                    <span className='text-gray-800 text-xs sm:text-sm'>
                      ₹{taxBreakdown.cgst.toFixed(2)}
                    </span>
                  </div>
                )}

                {taxBreakdown.sgst > 0 && (
                  <div className='flex justify-between py-1.5 sm:py-2'>
                    <span className='text-gray-600 text-xs sm:text-sm'>
                      SGST:
                    </span>
                    <span className='text-gray-800 text-xs sm:text-sm'>
                      ₹{taxBreakdown.sgst.toFixed(2)}
                    </span>
                  </div>
                )}

                {taxBreakdown.igst > 0 && (
                  <div className='flex justify-between py-1.5 sm:py-2'>
                    <span className='text-gray-600 text-xs sm:text-sm'>
                      IGST:
                    </span>
                    <span className='text-gray-800 text-xs sm:text-sm'>
                      ₹{taxBreakdown.igst.toFixed(2)}
                    </span>
                  </div>
                )}

                {taxBreakdown.cess > 0 && (
                  <div className='flex justify-between py-1.5 sm:py-2'>
                    <span className='text-gray-600 text-xs sm:text-sm'>
                      Cess:
                    </span>
                    <span className='text-gray-800 text-xs sm:text-sm'>
                      ₹{taxBreakdown.cess.toFixed(2)}
                    </span>
                  </div>
                )}

                {discountTexts.map((text, index) => (
                  <div
                    className='flex justify-between py-1.5 sm:py-2'
                    key={index}
                  >
                    <span className='text-gray-600 text-xs sm:text-sm'>
                      Discount:
                    </span>
                    <span className='text-gray-800 text-xs sm:text-sm'>
                      {text}
                    </span>
                  </div>
                ))}

                {effectiveTaxAmount > 0 && taxBreakupTotal <= 0 && (
                  <div className='flex justify-between py-1.5 sm:py-2'>
                    <span className='text-gray-600 text-xs sm:text-sm'>
                      Tax:
                    </span>
                    <span className='text-gray-800 text-xs sm:text-sm'>
                      ₹{effectiveTaxAmount.toFixed(2)}
                    </span>
                  </div>
                )}

                {shippingAmount > 0 && (
                  <div className='flex justify-between py-1.5 sm:py-2'>
                    <span className='text-gray-600 text-xs sm:text-sm'>
                      Shipping:
                    </span>
                    <span className='text-gray-800 text-xs sm:text-sm'>
                      ₹{shippingAmount.toFixed(2)}
                    </span>
                  </div>
                )}

                <div className='flex justify-between py-2 sm:py-3 border-t border-gray-200 mt-1.5 sm:mt-2'>
                  <span className='text-base sm:text-lg font-semibold text-gray-800'>
                    Total:
                  </span>
                  <span className='text-base sm:text-lg font-semibold text-gray-800'>
                    ₹
                    {(isDeliveryInvoiceMode
                      ? Number(selectedDeliveryInvoice?.amount || 0)
                      : Number(order.total || 0)
                    ).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            {activeStore.aadharPhoto && (
              <div className='flex bg-gray-100 rounded-lg'>
                <img
                  src={AWS_URL + activeStore.aadharPhoto}
                  alt='Payment QR Code'
                  className='w-30 object-contain'
                  crossOrigin='anonymous'
                />
              </div>
            )}
          </div>

          {/* Footer - Responsive */}
          <div className='mt-8 sm:mt-9 md:mt-10 pt-4 sm:pt-5 md:pt-6 border-t border-gray-200 text-center'>
            <p className='text-gray-600 text-xs sm:text-sm'>
              Thank you for shopping with us!
            </p>
            <p className='text-gray-500 text-[10px] sm:text-xs md:text-sm mt-1.5 sm:mt-2'>
              If you have any questions concerning this invoice, contact our
              support team.
            </p>
            <p className='text-gray-500 text-[10px] sm:text-xs md:text-sm mt-3 sm:mt-4'>
              Powered by Dukanbuddy™
            </p>
          </div>
        </div>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body * {
            visibility: hidden;
          }
          #invoice, #invoice * {
            visibility: visible;
          }
          #invoice {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            padding: 0;
            margin: 0;
            box-shadow: none;
          }
          .no-print {
            display: none !important;
          }
        }
      `}</style>
    </div>
  );
}

export default InvoicePage;
